import { gql, useQuery } from '@apollo/client';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const useBaseAndProposedSchemas = ({
  baseSchemaHash,
  proposedSchemaHash,
}: {
  baseSchemaHash?: string | null;
  proposedSchemaHash?: string | null;
}) => {
  const { currentVariantRouteConfig } = useInternalGraphLinking();
  const { graphId } = useRouteParams(currentVariantRouteConfig);

  const schemaHashesMatch = baseSchemaHash === proposedSchemaHash;

  const { data: documentData, loading: loadingDocumentData } = useQuery<
    GraphQLTypes.UseBaseAndProposedSchemaDocumentsQuery,
    GraphQLTypes.UseBaseAndProposedSchemaDocumentsQueryVariables
  >(
    gql`
      query UseBaseAndProposedSchemaDocumentsQuery(
        $graphId: ID!
        $baseSchemaHash: SHA256
        $proposedSchemaHash: SHA256
        $schemaHashesMatch: Boolean!
      ) {
        service(id: $graphId) {
          id
          baseSchema: doc(hash: $baseSchemaHash) {
            source
          }
          proposedSchema: doc(hash: $proposedSchemaHash)
            @skip(if: $schemaHashesMatch) {
            source
          }
        }
      }
    `,
    {
      variables: {
        graphId,
        baseSchemaHash,
        proposedSchemaHash,
        schemaHashesMatch,
      },
      skip: !baseSchemaHash && !proposedSchemaHash,
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const baseSchema = documentData?.service?.baseSchema?.source;
  return {
    baseSchema,
    proposedSchema: schemaHashesMatch
      ? baseSchema
      : documentData?.service?.proposedSchema?.source,
    loading: loadingDocumentData,
  };
};
